import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import { MentionsInput, Mention } from 'react-mentions';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot } from 'firebase/firestore';

import { useAppContext } from '../AppContext';
import UniversalMediaRenderer from './UniversalMediaRenderer';
import HoverTooltip from './HoverTooltip';
import './NewWhisperForm.css';

function NewWhisperForm({ initialText = '', remixingWhisper = null, onClose, onPostSuccess }) {
    const { t } = useTranslation();
    const { userId, uploadFile, appFunctions, setMessage, showConfirmation, db, appId, handlePageChange } = useAppContext();
    
    const [content, setContent] = useState(initialText);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [postTarget, setPostTarget] = useState('public');
    const [myNexuses, setMyNexuses] = useState([]);
    const [isLoadingNexuses, setIsLoadingNexuses] = useState(true);
    const [isSensitive, setIsSensitive] = useState(false);
    const [tags, setTags] = useState('');
    const [vibe, setVibe] = useState(null);

    const [loadingStates, setLoadingStates] = useState({
        tone: false,
        tags: false,
        vibe: false,
    });

    // Mock data for mentions - replace with actual user/hashtag fetching logic
    const users = []; 
    const hashtags = [];

    // Fetch user's nexuses for the post target dropdown
    useState(() => {
        if (!userId) return;
        const q = query(collection(db, `artifacts/${appId}/public/data/nexuses`), where('memberIds', 'array-contains', userId));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setMyNexuses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setIsLoadingNexuses(false);
        });
        return () => unsubscribe();
    }, [userId, db, appId]);

    const onDrop = useCallback(acceptedFiles => {
        if (mediaFiles.length + acceptedFiles.length > 4) {
            setMessage(t('journalEntryForm.maxFilesError'));
            return;
        }
        const newFiles = acceptedFiles.map(file => Object.assign(file, {
            preview: URL.createObjectURL(file)
        }));
        setMediaFiles(prev => [...prev, ...newFiles]);
    }, [mediaFiles.length, setMessage, t]);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({ 
        onDrop, 
        accept: { 'image/*': [] }, 
        multiple: true 
    });

    const removeMedia = (index) => {
        setMediaFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleAiTool = useCallback(async (tool) => {
        if (!content.trim()) {
            setMessage(t('journalEntryForm.aiToolNoContent'));
            return;
        }
        showConfirmation({
            message: t('journalEntryForm.aiToolConfirmation'),
            onConfirm: async () => {
                setLoadingStates(prev => ({ ...prev, [tool]: true }));
                const invokeAiHelper = httpsCallable(appFunctions, 'invokeWhisperAiHelper');
                try {
                    const result = await invokeAiHelper({ text: content, tool });
                    if (result.data.success) {
                        if (tool === 'improve_writing') {
                            setContent(result.data.suggestions[0] || content);
                        } else if (tool === 'suggest_tags') {
                            setTags(result.data.tags.join(', '));
                        } else if (tool === 'analyze_mood') {
                            setVibe(result.data.vibe);
                        }
                    }
                } catch (error) {
                    console.error(`Error with AI tool '${tool}':`, error);
                    setMessage(t('journalEntryForm.aiToolError', { error: error.message }));
                } finally {
                    setLoadingStates(prev => ({ ...prev, [tool]: false }));
                }
            }
        });
    }, [content, appFunctions, setMessage, showConfirmation, t]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        if (!content.trim() && mediaFiles.length === 0) {
            setMessage(t('journalEntryForm.noContentOrMedia'));
            return;
        }
        setIsSubmitting(true);

        try {
            let mediaUrls = [];
            let mediaPaths = [];

            for (const file of mediaFiles) {
                const fileExtension = file.name.split('.').pop();
                const mediaPath = `entries/${userId}/${Date.now()}.${fileExtension}`;
                // Note: `uploadFile` now needs to handle progress differently if needed.
                const mediaUrl = await uploadFile(file, mediaPath, () => {}); 
                mediaUrls.push(mediaUrl);
                mediaPaths.push(mediaPath);
            }

            const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean);
            const payload = {
                content: content.trim(),
                tags: tagsArray,
                mediaUrls: mediaUrls.length > 0 ? mediaUrls : null,
                mediaPaths: mediaPaths.length > 0 ? mediaPaths : null,
                vibe,
                sensitive: isSensitive,
                remixOf: remixingWhisper ? remixingWhisper.id : null,
            };

            if (postTarget === 'public') {
                const createWhisper = httpsCallable(appFunctions, 'createWhisper');
                const result = await createWhisper(payload);
                setMessage(t('journalEntryForm.postSuccess', { reward: result.data.reward }));
            } else {
                const postToNexus = httpsCallable(appFunctions, 'postToNexus');
                await postToNexus({ ...payload, nexusId: postTarget });
                setMessage(t('journalEntryForm.postToNexusSuccess'));
                handlePageChange('nexus', { nexusId: postTarget });
            }
            
            if (onPostSuccess) {
                onPostSuccess();
            }
            onClose(); // Close modal on success

        } catch (error) {
            console.error("Error creating post:", error);
            setMessage(t('journalEntryForm.postError', { error: error.message }));
        } finally {
            setIsSubmitting(false);
        }
    }, [content, tags, mediaFiles, userId, appFunctions, uploadFile, setMessage, postTarget, handlePageChange, vibe, isSensitive, remixingWhisper, onClose, onPostSuccess, t]);

    return (
        <div className="new-whisper-form">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="text-area-container">
                    <MentionsInput
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder={t('journalEntryForm.placeholder')}
                        className="mentions"
                        maxLength="280"
                    >
                        <Mention trigger="@" data={users} className="mentions__mention" />
                        <Mention trigger="#" data={hashtags} className="mentions__mention" />
                    </MentionsInput>
                    <span className={`char-counter ${content.length > 260 ? 'text-red-500' : 'text-gray-400'}`}>
                        {content.length} / 280
                    </span>
                </div>

                <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
                    <input {...getInputProps()} />
                    <LucideIcons.ImageUp size={48} className="mx-auto text-gray-500" />
                    <p>{t('journalEntryForm.dragAndDrop')}</p>
                </div>

                {mediaFiles.length > 0 && (
                    <div className="media-previews">
                        {mediaFiles.map((file, index) => (
                            <div key={index} className="media-preview-item">
                                <img src={file.preview} alt={`preview ${index}`} onLoad={() => URL.revokeObjectURL(file.preview)} />
                                <button type="button" onClick={() => removeMedia(index)} className="remove-media-btn">
                                    <LucideIcons.X size={14} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="ai-tools">
                    <HoverTooltip text={t('journalEntryForm.improveWritingTooltip')}>
                        <button type="button" onClick={() => handleAiTool('improve_writing')} disabled={loadingStates.tone || !content.trim()} className="ai-tool-btn">
                            {loadingStates.tone ? <div className="spinner" /> : <LucideIcons.Wand2 size={20} />}
                        </button>
                    </HoverTooltip>
                    <HoverTooltip text={t('journalEntryForm.suggestTagsTooltip')}>
                        <button type="button" onClick={() => handleAiTool('suggest_tags')} disabled={loadingStates.tags || !content.trim()} className="ai-tool-btn">
                            {loadingStates.tags ? <div className="spinner" /> : <LucideIcons.Tags size={20} />}
                        </button>
                    </HoverTooltip>
                    <HoverTooltip text={t('journalEntryForm.analyzeMoodTooltip')}>
                        <button type="button" onClick={() => handleAiTool('analyze_mood')} disabled={loadingStates.vibe || !content.trim()} className="ai-tool-btn">
                            {loadingStates.vibe ? <div className="spinner" /> : <LucideIcons.Smile size={20} />}
                        </button>
                    </HoverTooltip>
                </div>
                
                {vibe && <p className="vibe-display">{t('journalEntryForm.mood', { vibe })}</p>}

                <input 
                    type="text" 
                    className="tags-input" 
                    placeholder={t('journalEntryForm.tagsPlaceholder')} 
                    value={tags} 
                    onChange={(e) => setTags(e.target.value)} 
                />

                <div className="form-options">
                    <div className="sensitive-checkbox">
                        <input type="checkbox" id="sensitiveContent" checked={isSensitive} onChange={(e) => setIsSensitive(e.target.checked)} />
                        <label htmlFor="sensitiveContent">{t('journalEntryForm.sensitiveContent')}</label>
                    </div>
                    <select value={postTarget} onChange={(e) => setPostTarget(e.target.value)} className="post-target-select" disabled={isLoadingNexuses}>
                        <option value="public">{t('journalEntryForm.postToPublicFeed')}</option>
                        {myNexuses.map(nexus => <option key={nexus.id} value={nexus.id}>{t('journalEntryForm.postToNexus', { nexusName: nexus.name })}</option>)}
                    </select>
                </div>

                <button type="submit" disabled={isSubmitting} className="submit-btn">
                    {isSubmitting ? <div className="spinner" /> : <LucideIcons.Send size={18} />}
                    <span>{isSubmitting ? t('journalEntryForm.posting') : t('journalEntryForm.postWhisper')}</span>
                </button>
            </form>
        </div>
    );
}

export default NewWhisperForm;
